import { useToastStore } from '../stores/toastStore.js';
import { useServerStore } from '../stores/serverStore.js';
import { useDmStore } from '../stores/dmStore.js';
import { useUnreadStore } from '../stores/unreadStore.js';
import { useMessageStore } from '../stores/messageStore.js';
import { useVoiceStore } from '../stores/voiceStore.js';
import { getConnection } from './signalr.js';

export interface NotificationData {
  channelId?: string;
  messageId?: string;
  serverId?: string | null;
}

/**
 * Navigate to the channel/DM referenced by notification data.
 */
export function navigateToNotification(data: NotificationData) {
  if (!data.channelId) return;

  // Explicitly mark as read when navigating from a notification click.
  // We can't rely solely on the channel-change useEffect because that won't
  // fire when the target channel is already the active one (no state change).
  try {
    const conn = getConnection();
    if (conn.state === 'Connected') {
      conn.invoke('MarkChannelRead', data.channelId).catch(console.error);
    }
  } catch {
    // Connection not yet initialized (cold start) — the channel-change useEffect
    // will handle mark-as-read once the connection is established.
  }
  if (data.serverId) {
    useUnreadStore.getState().markChannelRead(data.channelId, data.serverId);
  } else {
    useUnreadStore.getState().markDmChannelRead(data.channelId);
  }

  if (data.serverId) {
    // Server channel notification
    const { servers, setActiveServer, channels, setActiveChannel } = useServerStore.getState();
    const server = servers.find((s) => s.id === data.serverId);
    if (server) {
      const currentServer = useServerStore.getState().activeServer;
      if (currentServer?.id === server.id) {
        // Already on this server, just switch channel
        const channel = channels.find((c) => c.id === data.channelId);
        if (channel) setActiveChannel(channel);
      } else {
        // Switch server, then find and select channel after channels load
        setActiveServer(server).then(() => {
          const ch = useServerStore.getState().channels.find((c) => c.id === data.channelId);
          if (ch) useServerStore.getState().setActiveChannel(ch);
        });
      }
    }
    useDmStore.getState().exitDmMode();
  } else {
    // DM notification — mirror the same steps as ChannelSidebar's handleDmClick:
    // leave current channel, enter DM mode, join + fetch the DM channel.
    const { dmChannels, setActiveDmChannel, enterDmMode } = useDmStore.getState();
    const dm = dmChannels.find((d) => d.id === data.channelId);
    if (dm) {
      const { currentChannelId, leaveChannel, joinChannel, fetchMessages } = useMessageStore.getState();
      if (currentChannelId) {
        leaveChannel(currentChannelId).catch(console.error);
      }
      enterDmMode();
      setActiveDmChannel(dm);
      joinChannel(dm.id).catch(console.error);
      fetchMessages(dm.id);
    }
  }
}

/**
 * Show a desktop notification (Electron only) and always show in-app toast.
 * Desktop notification only fires when the window is not focused.
 *
 * Uses synchronous DOM focus APIs (document.hasFocus / document.hidden) to
 * decide whether to dispatch the native pop — this avoids an `await`-on-IPC
 * round-trip that previously could stall here when the Linux notification
 * daemon was slow. The main process re-checks focus before showing as
 * defense-in-depth (see packages/desktop/src/main/notifications.ts).
 */
export const showDesktopNotification = (
  title: string,
  body: string,
  data?: NotificationData
) => {
  const onAction = data ? () => navigateToNotification(data) : undefined;
  useToastStore.getState().addToast(body, 'info', 4000, onAction, title);

  // If running in Electron, also fire the native notification when the window
  // isn't currently focused/visible — unless the user has disabled native
  // notifications (escape hatch for broken Linux notification daemons).
  if (typeof window !== 'undefined' && window.electron) {
    try {
      if (!useVoiceStore.getState().nativeDesktopNotificationsEnabled) return;
      const isHidden = typeof document !== 'undefined'
        ? (document.hidden || !document.hasFocus())
        : false;
      if (!isHidden) return;
      window.electron.showNotification(title, body, data);
    } catch (error) {
      console.error('[ElectronNotifications] Failed to show notification:', error);
    }
  }
};

/**
 * Set up listener for OS notification clicks (Electron only).
 * Call once at app startup. Returns an unsubscribe function.
 */
export function setupNotificationClickListener(): (() => void) | undefined {
  if (typeof window !== 'undefined' && window.electron?.onNotificationClicked) {
    return window.electron.onNotificationClicked((data: NotificationData) => {
      navigateToNotification(data);
    });
  }
}

/**
 * Check if running in Electron environment
 */
export const isElectron = (): boolean => {
  return typeof window !== 'undefined' && !!window.electron;
};
